import { differenceInDays } from 'date-fns';

/** 一度に取得できる最大日数。UI のエラーメッセージ「最大取得可能日数の10日」と対応する。 */
export const MAX_PLANNING_RANGE_DAYS = 10;

export type NormalizedEndOn = {
  endOn: string;
  /** 引数の endOn から変わったか。true のとき呼び出し側は URL を貼り替える。 */
  changed: boolean;
};

/**
 * 配車系画面（/plans・/timeline・/convenience-dispatches）の URL 日付レンジを
 * 表示可能な範囲へ正規化する。終了日が開始日より前・上限日数を超過・日付として
 * 解釈できない場合は、終了日を開始日へ揃える。
 *
 * startOn 自体の妥当性は見ない。呼び出し側が DateModel#isValid で判定し、
 * 不正なら設定日へフォールバックする既存の流れを踏襲する。
 *
 * Why not 各画面での個別判定: 3 画面がそれぞれ判定を持った結果、/timeline は
 * 不正レンジのまま描画してクラッシュし、/convenience-dispatches は判定自体が
 * 無い、という差異が生まれた。判定をここへ集約して挙動を揃える。
 */
export function normalizePlanningEndOn(startOn: string, endOn: string | undefined): NormalizedEndOn {
  if (endOn === undefined) return { endOn: startOn, changed: true };

  const dateDiff = differenceInDays(new Date(endOn), new Date(startOn));
  if (Number.isNaN(dateDiff) || dateDiff < 0 || dateDiff >= MAX_PLANNING_RANGE_DAYS) {
    return { endOn: startOn, changed: true };
  }

  return { endOn, changed: false };
}

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * タイムラインカレンダーの表示日数（終了日を含む）を返す。
 *
 * FullCalendar の view duration は 0 日以下だと描画時に例外になり画面が白くなるため、
 * 下限を 1 日に切り上げる。呼び出し元は normalizePlanningEndOn で不正レンジを弾いて
 * いるが、URL 正規化前の初回レンダーや Invalid Date のような想定外の値が来ても
 * 画面ごと落とさないための多層防御。
 */
export function calendarDurationDays(startDate: Date, endDate: Date): number {
  const diffDays = Math.ceil((endDate.getTime() - startDate.getTime()) / MILLISECONDS_PER_DAY) + 1;

  return Number.isFinite(diffDays) ? Math.max(1, diffDays) : 1;
}
